import { useState, useEffect, useCallback } from "react";

// ─── CONFIGURE THIS ─────────────────────────────────────
// For local dev with PHP running on a different port, use the full URL:
//   const API_BASE = "http://localhost:8000/api";
// For production on IONOS where React build + PHP are on same domain:
//   const API_BASE = "/api";
const API_BASE = "/api";
// ─────────────────────────────────────────────────────────

const PRESET_SUBSECTIONS = [
  { name: "Strengthen & Weaken", icon: "💪" },
  { name: "Assumption Questions", icon: "🔍" },
  { name: "Flaw in Reasoning", icon: "⚡" },
  { name: "Inference / Must Be True", icon: "🧠" },
  { name: "Parallel Reasoning", icon: "🔄" },
  { name: "Main Point / Conclusion", icon: "🎯" },
  { name: "Comparative Passages", icon: "📊" },
  { name: "Science Passages", icon: "🔬" },
  { name: "Law Passages", icon: "⚖️" },
  { name: "Timed Drills", icon: "⏱️" },
];

const COLORS = ["#C8A96E","#7BA39E","#9B7CB8","#C47A6E","#6E8FC4","#C4A06E","#8BC47A","#C46EB8","#6EC4C4","#C46E6E"];

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...options,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

function CircularProgress({ percentage, color, size = 120, strokeWidth = 8 }) {
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const o = c - (Math.min(percentage, 100) / 100) * c;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={o}
        style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)" }} />
    </svg>
  );
}

function BarProgress({ percentage, color }) {
  return (
    <div style={{ width: "100%", height: 6, borderRadius: 3, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
      <div style={{ width: `${Math.min(percentage,100)}%`, height: "100%", borderRadius: 3,
        background: `linear-gradient(90deg, ${color}, ${color}cc)`,
        transition: "width 0.8s cubic-bezier(0.4,0,0.2,1)" }} />
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <div style={{ width: 32, height: 32, border: "3px solid rgba(200,169,110,0.2)",
        borderTopColor: "#C8A96E", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [authForm, setAuthForm] = useState({ email: "", username: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const [sections, setSections] = useState([]);
  const [logs, setLogs] = useState([]);
  const [overallGoal, setOverallGoal] = useState(200);
  const [view, setView] = useState("dashboard");
  const [loading, setLoading] = useState(true);

  const [showAddSection, setShowAddSection] = useState(false);
  const [showLogHours, setShowLogHours] = useState(null);
  const [showEditGoal, setShowEditGoal] = useState(null);
  const [showPresets, setShowPresets] = useState(false);
  const [newSection, setNewSection] = useState({ name: "", goalHours: 10, icon: "📚", color: COLORS[0] });
  const [logInput, setLogInput] = useState({ hours: "", minutes: "", note: "" });
  const [goalInput, setGoalInput] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Check auth on mount
  useEffect(() => {
    api("/auth.php?action=me")
      .then(d => { if (d.authenticated) { setUser(d.user); setOverallGoal(d.user.overall_goal || 200); } })
      .catch(() => {})
      .finally(() => setAuthChecked(true));
  }, []);

  // Load data when user logs in
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([api("/sections.php"), api("/logs.php"), api("/settings.php")])
      .then(([secs, lgs, settings]) => { setSections(secs); setLogs(lgs); setOverallGoal(settings.overallGoal || 200); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const handleAuth = async (e) => {
    e.preventDefault(); setAuthError(""); setAuthLoading(true);
    try {
      const action = authMode === "login" ? "login" : "register";
      const payload = authMode === "login" ? { email: authForm.email, password: authForm.password } : authForm;
      const data = await api(`/auth.php?action=${action}`, { method: "POST", body: JSON.stringify(payload) });
      setUser(data.user);
    } catch (err) { setAuthError(err.message); }
    finally { setAuthLoading(false); }
  };

  const handleLogout = async () => {
    await api("/auth.php?action=logout", { method: "POST" }).catch(() => {});
    setUser(null); setSections([]); setLogs([]); setAuthForm({ email: "", username: "", password: "" });
  };

  const addSection = useCallback(async () => {
    if (!newSection.name.trim()) return;
    try {
      const data = await api("/sections.php", { method: "POST", body: JSON.stringify({
        name: newSection.name.trim(), icon: newSection.icon, color: newSection.color,
        goalHours: Math.max(1, Number(newSection.goalHours) || 10),
      })});
      setSections(p => [...p, data.section]);
      setNewSection({ name: "", goalHours: 10, icon: "📚", color: COLORS[Math.floor(Math.random()*COLORS.length)] });
      setShowAddSection(false);
    } catch(e) { console.error(e); }
  }, [newSection]);

  const logHours = useCallback(async (sectionId) => {
    const h = Number(logInput.hours)||0, m = Number(logInput.minutes)||0, total = h + m/60;
    if (total <= 0) return;
    try {
      const data = await api("/logs.php", { method: "POST", body: JSON.stringify({ sectionId, hours: Math.round(total*100)/100, note: logInput.note }) });
      setLogs(p => [data.log, ...p]);
      setSections(p => p.map(s => s.id===sectionId ? { ...s, loggedHours: Math.round((s.loggedHours+total)*100)/100 } : s));
      setLogInput({ hours: "", minutes: "", note: "" }); setShowLogHours(null);
    } catch(e) { console.error(e); }
  }, [logInput]);

  const updateGoal = useCallback(async (sectionId) => {
    const val = Math.max(0, Number(goalInput)||0);
    try {
      await api(`/sections.php?id=${sectionId}`, { method: "PUT", body: JSON.stringify({ goalHours: val }) });
      setSections(p => p.map(s => s.id===sectionId ? { ...s, goalHours: val } : s));
      setShowEditGoal(null); setGoalInput("");
    } catch(e) { console.error(e); }
  }, [goalInput]);

  const updateOverallGoal = useCallback(async (val) => {
    const goal = Math.max(1, Number(val)||1); setOverallGoal(goal);
    try { await api("/settings.php", { method: "PUT", body: JSON.stringify({ overallGoal: goal }) }); } catch(e) { console.error(e); }
  }, []);

  const updateSectionGoalInline = useCallback(async (sid, val) => {
    const goal = Math.max(0, Number(val)||0);
    setSections(p => p.map(s => s.id===sid ? { ...s, goalHours: goal } : s));
    try { await api(`/sections.php?id=${sid}`, { method: "PUT", body: JSON.stringify({ goalHours: goal }) }); } catch(e) { console.error(e); }
  }, []);

  const deleteSection = useCallback(async (sid) => {
    try {
      await api(`/sections.php?id=${sid}`, { method: "DELETE" });
      setSections(p => p.filter(s => s.id!==sid)); setLogs(p => p.filter(l => l.sectionId!==sid)); setConfirmDelete(null);
    } catch(e) { console.error(e); }
  }, []);

  const undoLog = useCallback(async (entry) => {
    try {
      await api(`/logs.php?id=${entry.id}`, { method: "DELETE" });
      setSections(p => p.map(s => s.id===entry.sectionId ? { ...s, loggedHours: Math.max(0, Math.round((s.loggedHours-entry.hours)*100)/100) } : s));
      setLogs(p => p.filter(l => l.id!==entry.id));
    } catch(e) { console.error(e); }
  }, []);

  const totalLogged = sections.reduce((s, sec) => s + sec.loggedHours, 0);
  const totalPct = overallGoal > 0 ? (totalLogged / overallGoal) * 100 : 0;

  const formatHours = (h) => {
    if (h===0) return "0h";
    const hrs = Math.floor(h), mins = Math.round((h-hrs)*60);
    if (hrs===0) return `${mins}m`; if (mins===0) return `${hrs}h`; return `${hrs}h ${mins}m`;
  };
  const formatDate = (iso) => {
    const d = new Date(iso), diff = Date.now()-d;
    if (diff<60000) return "Just now"; if (diff<3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff<86400000) return `${Math.floor(diff/3600000)}h ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Shared styles
  const b = { border: "none", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s ease", outline: "none" };
  const mo = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 };
  const mb = { background: "#1a1d23", borderRadius: 16, padding: "28px", maxWidth: 420, width: "100%", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 24px 80px rgba(0,0,0,0.5)" };
  const inp = { width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#e8e6e1", fontSize: 14, fontFamily: "'DM Sans', sans-serif", outline: "none", boxSizing: "border-box" };

  // ─── LOADING CHECK ────────────────────────────────────
  if (!authChecked) return (
    <div style={{ minHeight: "100vh", background: "#0f1115", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spinner />
    </div>
  );

  // ─── AUTH SCREEN ──────────────────────────────────────
  if (!user) return (
    <div style={{ minHeight: "100vh", background: "#0f1115", color: "#e8e6e1", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ maxWidth: 400, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, margin: "0 0 8px",
            background: "linear-gradient(135deg, #C8A96E 0%, #e8d5a8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            LSAT Study Tracker
          </h1>
          <p style={{ color: "#888", fontSize: 14, letterSpacing: "0.05em", textTransform: "uppercase" }}>Track · Focus · Conquer</p>
        </div>
        <div style={{ background: "#1a1d23", borderRadius: 16, padding: 28, border: "1px solid rgba(200,169,110,0.1)" }}>
          <div style={{ display: "flex", gap: 0, marginBottom: 24, background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 3 }}>
            {["login","register"].map(m => (
              <button key={m} onClick={() => { setAuthMode(m); setAuthError(""); }}
                style={{ ...b, flex: 1, padding: "10px", borderRadius: 8, fontSize: 14, fontWeight: 500,
                  background: authMode===m ? "rgba(200,169,110,0.15)" : "transparent",
                  color: authMode===m ? "#C8A96E" : "#888" }}>
                {m==="login" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>
          <form onSubmit={handleAuth}>
            {authMode==="register" && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Name</label>
                <input type="text" value={authForm.username} onChange={e => setAuthForm(p=>({...p, username: e.target.value}))}
                  placeholder="Your name" style={inp} required />
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Email</label>
              <input type="email" value={authForm.email} onChange={e => setAuthForm(p=>({...p, email: e.target.value}))}
                placeholder="you@email.com" style={inp} required />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Password</label>
              <input type="password" value={authForm.password} onChange={e => setAuthForm(p=>({...p, password: e.target.value}))}
                placeholder="••••••••" style={inp} required minLength={6} />
            </div>
            {authError && (
              <div style={{ background: "rgba(196,106,110,0.1)", border: "1px solid rgba(196,106,110,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#C47A6E" }}>{authError}</div>
            )}
            <button type="submit" disabled={authLoading} style={{
              ...b, width: "100%", padding: "12px", borderRadius: 10, fontSize: 15, fontWeight: 600,
              background: "linear-gradient(135deg, rgba(200,169,110,0.25), rgba(200,169,110,0.15))",
              color: "#C8A96E", opacity: authLoading ? 0.6 : 1 }}>
              {authLoading ? "..." : authMode==="login" ? "Sign In" : "Create Account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  // ─── DATA LOADING ─────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#0f1115", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spinner />
    </div>
  );

  // ─── MAIN APP ─────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#0f1115", color: "#e8e6e1", fontFamily: "'DM Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #141720 0%, #1a1d25 100%)", borderBottom: "1px solid rgba(200,169,110,0.15)", padding: "24px 28px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, margin: 0,
              background: "linear-gradient(135deg, #C8A96E 0%, #e8d5a8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              LSAT Study Tracker
            </h1>
            <p style={{ color: "#888", fontSize: 13, margin: "4px 0 0" }}>Welcome back, <span style={{ color: "#C8A96E" }}>{user.username}</span></p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {["dashboard","history","settings"].map(v => (
              <button key={v} onClick={() => setView(v)} style={{
                ...b, padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 500,
                background: view===v ? "rgba(200,169,110,0.15)" : "transparent",
                color: view===v ? "#C8A96E" : "#888", textTransform: "capitalize" }}>
                {v}
              </button>
            ))}
            <button onClick={handleLogout} style={{ ...b, padding: "8px 12px", borderRadius: 8, fontSize: 12, background: "rgba(255,255,255,0.04)", color: "#666", marginLeft: 4 }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 20px 60px" }}>

        {/* ─── DASHBOARD ──────────────────────────── */}
        {view==="dashboard" && (<>
          {/* Overall Progress */}
          <div style={{ background: "linear-gradient(135deg, #1a1d25 0%, #1e2130 100%)", borderRadius: 16, padding: "28px", marginBottom: 24,
            border: "1px solid rgba(200,169,110,0.1)", display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <CircularProgress percentage={totalPct} color="#C8A96E" size={110} strokeWidth={7} />
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: "#C8A96E" }}>{Math.round(totalPct)}%</span>
                <span style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>Overall</span>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 600 }}>Overall Progress</h2>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div><div style={{ fontSize: 24, fontWeight: 700, color: "#C8A96E" }}>{formatHours(totalLogged)}</div><div style={{ fontSize: 12, color: "#888" }}>studied</div></div>
                <div><div style={{ fontSize: 24, fontWeight: 700, color: "#666" }}>{formatHours(overallGoal)}</div><div style={{ fontSize: 12, color: "#888" }}>goal</div></div>
                <div><div style={{ fontSize: 24, fontWeight: 700, color: "#7BA39E" }}>{sections.length}</div><div style={{ fontSize: 12, color: "#888" }}>sections</div></div>
              </div>
            </div>
          </div>

          {/* Section Cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
            {sections.map(sec => {
              const pct = sec.goalHours>0 ? (sec.loggedHours/sec.goalHours)*100 : 0;
              const rem = Math.max(0, sec.goalHours-sec.loggedHours);
              const sLogs = logs.filter(l => l.sectionId===sec.id).slice(0,5);
              return (
                <div key={sec.id} style={{ background: "#1a1d23", borderRadius: 14, padding: "20px 22px", border: `1px solid ${sec.color}18` }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 20 }}>{sec.icon}</span>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{sec.name}</h3>
                        <span style={{ fontSize: 12, color: "#888" }}>
                          {formatHours(sec.loggedHours)} / {formatHours(sec.goalHours)}
                          {rem>0 && <span style={{ color: sec.color, marginLeft: 6 }}>({formatHours(rem)} left)</span>}
                          {pct>=100 && <span style={{ color: "#7BA39E", marginLeft: 6 }}>✓ Complete!</span>}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => { setShowLogHours(sec.id); setLogInput({ hours:"", minutes:"", note:"" }); }}
                        style={{ ...b, padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: `${sec.color}20`, color: sec.color }}>+ Log</button>
                      <button onClick={() => { setShowEditGoal(sec.id); setGoalInput(String(sec.goalHours)); }}
                        style={{ ...b, padding: "6px 10px", borderRadius: 8, fontSize: 12, background: "rgba(255,255,255,0.04)", color: "#888" }}>Edit Goal</button>
                      <button onClick={() => setConfirmDelete(sec.id)}
                        style={{ ...b, padding: "6px 10px", borderRadius: 8, fontSize: 12, background: "rgba(255,255,255,0.04)", color: "#666" }}>×</button>
                    </div>
                  </div>
                  <BarProgress percentage={pct} color={sec.color} />
                  <div style={{ textAlign: "right", marginTop: 6 }}><span style={{ fontSize: 13, fontWeight: 600, color: sec.color }}>{Math.round(pct)}%</span></div>
                  {sLogs.length>0 && (
                    <div style={{ marginTop: 12, borderTop: `1px solid ${sec.color}10`, paddingTop: 10 }}>
                      {sLogs.map((log,i) => (
                        <div key={log.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 0",
                          borderBottom: i<sLogs.length-1 ? "1px solid rgba(255,255,255,0.03)" : "none" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 4, height: 4, borderRadius: 2, background: sec.color, opacity: 0.5, flexShrink: 0 }} />
                            <span style={{ fontSize: 12, color: "#999" }}>{formatHours(log.hours)}</span>
                            {log.note && <span style={{ fontSize: 12, color: "#666" }}>— {log.note}</span>}
                          </div>
                          <span style={{ fontSize: 11, color: "#555", flexShrink: 0 }}>{formatDate(log.date)}</span>
                        </div>
                      ))}
                      {logs.filter(l=>l.sectionId===sec.id).length>5 && (
                        <button onClick={() => setView("history")} style={{ ...b, fontSize: 11, color: "#666", padding: "4px 0", marginTop: 2, background: "none" }}>View all →</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={() => setShowAddSection(true)} style={{
              ...b, padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600,
              background: "rgba(200,169,110,0.1)", color: "#C8A96E", border: "1px dashed rgba(200,169,110,0.3)" }}>+ Add Custom Section</button>
            <button onClick={() => setShowPresets(true)} style={{
              ...b, padding: "12px 20px", borderRadius: 10, fontSize: 13, fontWeight: 500,
              background: "rgba(255,255,255,0.04)", color: "#888", border: "1px solid rgba(255,255,255,0.08)" }}>Browse Presets</button>
          </div>
        </>)}

        {/* ─── HISTORY ────────────────────────────── */}
        {view==="history" && (
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, marginBottom: 16 }}>Study Log</h2>
            {logs.length===0 ? (
              <div style={{ textAlign: "center", padding: 48, color: "#555" }}>
                <p style={{ fontSize: 40, margin: "0 0 12px" }}>📚</p>
                <p>No study sessions logged yet.</p>
                <p style={{ fontSize: 13 }}>Head to the dashboard and log your first session!</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {logs.map(log => {
                  const sec = sections.find(s => s.id===log.sectionId);
                  return (
                    <div key={log.id} style={{ background: "#1a1d23", borderRadius: 12, padding: "14px 18px",
                      border: "1px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
                          background: sec ? `${sec.color}15` : `${log.sectionColor||'#666'}15`, fontSize: 16 }}>
                          {sec?.icon || log.sectionIcon || "📚"}
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{sec?.name || log.sectionName || "Deleted Section"}</div>
                          <div style={{ fontSize: 12, color: "#888" }}>
                            {formatHours(log.hours)}
                            {log.note && <span style={{ marginLeft: 8, color: "#666" }}>— {log.note}</span>}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 12, color: "#666" }}>{formatDate(log.date)}</span>
                        <button onClick={() => undoLog(log)} style={{ ...b, padding: "4px 8px", borderRadius: 6, fontSize: 11,
                          background: "rgba(196,106,110,0.1)", color: "#C47A6E" }}>Undo</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ─── SETTINGS ───────────────────────────── */}
        {view==="settings" && (
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, marginBottom: 20 }}>Settings</h2>
            {/* Account */}
            <div style={{ background: "#1a1d23", borderRadius: 14, padding: "24px", border: "1px solid rgba(255,255,255,0.06)", marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 600 }}>Account</h3>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                <div><span style={{ fontSize: 12, color: "#888" }}>Name</span><div style={{ fontSize: 14, marginTop: 2 }}>{user.username}</div></div>
                <div><span style={{ fontSize: 12, color: "#888" }}>Email</span><div style={{ fontSize: 14, marginTop: 2 }}>{user.email}</div></div>
              </div>
            </div>
            {/* Overall Goal */}
            <div style={{ background: "#1a1d23", borderRadius: 14, padding: "24px", border: "1px solid rgba(200,169,110,0.1)", marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 600 }}>Overall Study Goal</h3>
              <p style={{ color: "#888", fontSize: 13, margin: "0 0 16px" }}>Set your total target hours. This drives the main progress ring.</p>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <input type="number" min="1" value={overallGoal} onChange={e => updateOverallGoal(e.target.value)}
                  style={{ ...inp, width: 120, fontSize: 20, textAlign: "center", fontWeight: 600 }} />
                <span style={{ color: "#888", fontSize: 14 }}>hours</span>
              </div>
              <div style={{ marginTop: 14 }}>
                <BarProgress percentage={totalPct} color="#C8A96E" />
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                  <span style={{ fontSize: 12, color: "#888" }}>{formatHours(totalLogged)} studied</span>
                  <span style={{ fontSize: 12, color: "#C8A96E" }}>{Math.round(totalPct)}% complete</span>
                </div>
              </div>
              <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[100,150,200,300,400,500].map(h => (
                  <button key={h} onClick={() => updateOverallGoal(h)} style={{
                    ...b, padding: "6px 14px", borderRadius: 8, fontSize: 12,
                    background: overallGoal===h ? "rgba(200,169,110,0.2)" : "rgba(255,255,255,0.04)",
                    color: overallGoal===h ? "#C8A96E" : "#888",
                    border: overallGoal===h ? "1px solid rgba(200,169,110,0.3)" : "1px solid transparent" }}>{h}h</button>
                ))}
              </div>
            </div>
            {/* Section Goals */}
            <div style={{ background: "#1a1d23", borderRadius: 14, padding: "24px", border: "1px solid rgba(255,255,255,0.06)", marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 600 }}>Section Goals Breakdown</h3>
              <p style={{ color: "#888", fontSize: 13, margin: "0 0 16px" }}>Allocate your {overallGoal}h across study areas.</p>
              {sections.map(sec => (
                <div key={sec.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>{sec.icon}</span>
                  <span style={{ flex: 1, fontSize: 14 }}>{sec.name}</span>
                  <input type="number" min="0" value={sec.goalHours} onChange={e => updateSectionGoalInline(sec.id, e.target.value)}
                    style={{ ...inp, width: 70, textAlign: "center", fontSize: 14 }} />
                  <span style={{ fontSize: 12, color: "#666", width: 16 }}>h</span>
                </div>
              ))}
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", marginTop: 14, paddingTop: 14, display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, color: "#888" }}>Section goals total</span>
                <span style={{ fontSize: 14, fontWeight: 600,
                  color: sections.reduce((s,sec) => s+sec.goalHours, 0) > overallGoal ? "#C47A6E" : "#7BA39E" }}>
                  {formatHours(sections.reduce((s,sec) => s+sec.goalHours, 0))} / {formatHours(overallGoal)}
                </span>
              </div>
            </div>
            {/* Sign out */}
            <div style={{ background: "#1a1d23", borderRadius: 14, padding: "24px", border: "1px solid rgba(196,106,110,0.1)" }}>
              <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 600, color: "#C47A6E" }}>Account Actions</h3>
              <p style={{ color: "#888", fontSize: 13, margin: "0 0 16px" }}>Sign out. Your data is saved on the server.</p>
              <button onClick={handleLogout} style={{ ...b, padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: "rgba(196,106,110,0.15)", color: "#C47A6E" }}>Sign Out</button>
            </div>
          </div>
        )}
      </div>

      {/* ─── MODALS ──────────────────────────────────── */}
      {showLogHours && (
        <div style={mo} onClick={() => setShowLogHours(null)}>
          <div style={mb} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 20px", fontSize: 18, fontWeight: 600 }}>
              Log Study Time — {sections.find(s=>s.id===showLogHours)?.icon} {sections.find(s=>s.id===showLogHours)?.name}
            </h3>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Hours</label>
                <input type="number" min="0" placeholder="0" value={logInput.hours} onChange={e => setLogInput(p=>({...p, hours: e.target.value}))} style={inp} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Minutes</label>
                <input type="number" min="0" max="59" placeholder="0" value={logInput.minutes} onChange={e => setLogInput(p=>({...p, minutes: e.target.value}))} style={inp} />
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Note (optional)</label>
              <input type="text" placeholder="e.g., Chapter 5 practice problems" value={logInput.note} onChange={e => setLogInput(p=>({...p, note: e.target.value}))} style={inp} />
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
              {[15,30,45,60,90,120].map(mins => (
                <button key={mins} onClick={() => setLogInput(p=>({...p, hours: String(Math.floor(mins/60)), minutes: String(mins%60)}))}
                  style={{ ...b, padding: "6px 12px", borderRadius: 6, fontSize: 12, background: "rgba(255,255,255,0.06)", color: "#aaa" }}>
                  {mins<60 ? `${mins}m` : `${mins/60}h`}{mins===90 ? " (1h30m)" : ""}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowLogHours(null)} style={{ ...b, flex: 1, padding: "10px", borderRadius: 8, fontSize: 14, background: "rgba(255,255,255,0.06)", color: "#888" }}>Cancel</button>
              <button onClick={() => logHours(showLogHours)} style={{ ...b, flex: 1, padding: "10px", borderRadius: 8, fontSize: 14, fontWeight: 600, background: "rgba(200,169,110,0.2)", color: "#C8A96E" }}>Log Time</button>
            </div>
          </div>
        </div>
      )}

      {showEditGoal && (
        <div style={mo} onClick={() => setShowEditGoal(null)}>
          <div style={mb} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600 }}>Set Goal Hours</h3>
            <input type="number" min="0" value={goalInput} onChange={e => setGoalInput(e.target.value)}
              style={{ ...inp, marginBottom: 18, fontSize: 18, textAlign: "center" }} placeholder="Hours" />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowEditGoal(null)} style={{ ...b, flex: 1, padding: "10px", borderRadius: 8, background: "rgba(255,255,255,0.06)", color: "#888" }}>Cancel</button>
              <button onClick={() => updateGoal(showEditGoal)} style={{ ...b, flex: 1, padding: "10px", borderRadius: 8, fontWeight: 600, background: "rgba(200,169,110,0.2)", color: "#C8A96E" }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showAddSection && (
        <div style={mo} onClick={() => setShowAddSection(false)}>
          <div style={mb} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 20px", fontSize: 18, fontWeight: 600 }}>Add Custom Section</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Section Name</label>
              <input type="text" value={newSection.name} onChange={e => setNewSection(p=>({...p, name: e.target.value}))} placeholder="e.g., Assumption Questions" style={inp} />
            </div>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Goal (hours)</label>
                <input type="number" min="1" value={newSection.goalHours} onChange={e => setNewSection(p=>({...p, goalHours: e.target.value}))} style={inp} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>Icon</label>
                <input type="text" value={newSection.icon} onChange={e => setNewSection(p=>({...p, icon: e.target.value}))} style={{ ...inp, textAlign: "center", fontSize: 18 }} />
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 6 }}>Color</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {COLORS.map(c => (
                  <button key={c} onClick={() => setNewSection(p=>({...p, color: c}))} style={{
                    ...b, width: 28, height: 28, borderRadius: 8, background: c,
                    border: newSection.color===c ? "2px solid #fff" : "2px solid transparent",
                    transform: newSection.color===c ? "scale(1.1)" : "scale(1)" }} />
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowAddSection(false)} style={{ ...b, flex: 1, padding: "10px", borderRadius: 8, background: "rgba(255,255,255,0.06)", color: "#888" }}>Cancel</button>
              <button onClick={addSection} style={{ ...b, flex: 1, padding: "10px", borderRadius: 8, fontWeight: 600, background: "rgba(200,169,110,0.2)", color: "#C8A96E" }}>Add Section</button>
            </div>
          </div>
        </div>
      )}

      {showPresets && (
        <div style={mo} onClick={() => setShowPresets(false)}>
          <div style={mb} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 600 }}>LSAT Study Presets</h3>
            <p style={{ color: "#888", fontSize: 13, margin: "0 0 16px" }}>Tap to add common LSAT study areas</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflowY: "auto" }}>
              {PRESET_SUBSECTIONS.map(preset => {
                const exists = sections.some(s => s.name===preset.name);
                return (
                  <button key={preset.name} disabled={exists} onClick={async () => {
                    if (!exists) {
                      try {
                        const data = await api("/sections.php", { method: "POST", body: JSON.stringify({
                          name: preset.name, icon: preset.icon, color: COLORS[Math.floor(Math.random()*COLORS.length)], goalHours: 10 })});
                        setSections(p => [...p, data.section]);
                      } catch(e) { console.error(e); }
                    }
                  }} style={{ ...b, padding: "12px 16px", borderRadius: 10, textAlign: "left",
                    background: exists ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.04)",
                    color: exists ? "#555" : "#e8e6e1", display: "flex", alignItems: "center", gap: 10, opacity: exists ? 0.5 : 1 }}>
                    <span style={{ fontSize: 18 }}>{preset.icon}</span>
                    <span style={{ fontSize: 14 }}>{preset.name}</span>
                    {exists && <span style={{ marginLeft: "auto", fontSize: 11, color: "#555" }}>Added</span>}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setShowPresets(false)} style={{ ...b, width: "100%", padding: "10px", borderRadius: 8, marginTop: 14, background: "rgba(255,255,255,0.06)", color: "#888" }}>Done</button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div style={mo} onClick={() => setConfirmDelete(null)}>
          <div style={mb} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 10px", fontSize: 18 }}>Delete Section?</h3>
            <p style={{ color: "#888", fontSize: 14, margin: "0 0 20px" }}>
              This will remove "{sections.find(s=>s.id===confirmDelete)?.name}" and all its logged hours. This can't be undone.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmDelete(null)} style={{ ...b, flex: 1, padding: "10px", borderRadius: 8, background: "rgba(255,255,255,0.06)", color: "#888" }}>Keep</button>
              <button onClick={() => deleteSection(confirmDelete)} style={{ ...b, flex: 1, padding: "10px", borderRadius: 8, fontWeight: 600, background: "rgba(196,106,110,0.15)", color: "#C47A6E" }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
